<template>
  <label :data-field-key="fieldKey" class="block mb-3">
    <span class="form-label flex items-center justify-between gap-2 mb-1">
      <span class="flex items-center gap-2">
        <span>{{ defn.label || fieldKey }}</span>
        <span
          v-if="isTemplateDefault"
          class="badge badge-default badge-sm"
          title="This is the template's default — replace it with your own copy.">
          <svg class="w-2.5 h-2.5" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
            <path d="M10 2a1 1 0 011 1v1.07A7.002 7.002 0 0117 11a1 1 0 11-2 0 5 5 0 10-10 0 1 1 0 11-2 0 7.002 7.002 0 016-6.93V3a1 1 0 011-1zm0 6a3 3 0 100 6 3 3 0 000-6z" />
          </svg>
          Template default
        </span>
      </span>
      <StylePopover
        v-if="showStyleControls"
        :model-value="styleOverride || {}"
        :kind="defn.type === 'image' ? 'image' : 'text'"
        :swatches="brandSwatches"
        @update:model-value="$emit('update:styleOverride', $event)" />
    </span>

    <RichTextField
      v-if="defn.type === 'richtext'"
      :model-value="modelValue"
      @update:model-value="$emit('update:modelValue', $event)" />

    <textarea
      v-else-if="isLongText"
      :value="modelValue"
      :name="fieldKey"
      rows="3"
      class="form-input w-full"
      @focus="$emit('focus')"
      @input="$emit('preview', $event.target.value)"
      @blur="commit($event.target.value)" />

    <input
      v-else-if="defn.type === 'color'"
      :value="modelValue || '#000000'"
      :name="fieldKey"
      type="color"
      class="block h-10 w-20 border-gray-300 rounded-md"
      @input="$emit('update:modelValue', $event.target.value)">

    <ImageField
      v-else-if="defn.type === 'image'"
      :model-value="modelValue"
      :alt="altValue"
      :defn="defn"
      @update:model-value="$emit('update:modelValue', $event)"
      @update:alt="$emit('update:alt', $event)"
      @generate="$emit('generate')" />

    <!-- Link fields: pick a site page or enter a custom URL (no raw href typing). -->
    <div v-else-if="defn.type === 'url'" class="space-y-1">
      <select
        :value="linkMode === 'custom' ? '__custom__' : modelValue"
        :name="fieldKey"
        class="form-select w-full"
        @change="onLinkSelect($event.target.value)">
        <option value="">— No link —</option>
        <option v-for="p in pageLinks" :key="p.href" :value="p.href">{{ p.label }}</option>
        <option value="__custom__">Custom URL…</option>
      </select>
      <input
        v-if="linkMode === 'custom'"
        :value="modelValue"
        :name="`${fieldKey}_custom`"
        type="text"
        placeholder="https://…   ·   tel:+1…   ·   #section"
        class="form-input w-full"
        @focus="$emit('focus')"
        @input="$emit('preview', $event.target.value)"
        @blur="commit($event.target.value)">
      <span v-if="linkWarning" class="form-error">{{ linkWarning }}</span>
    </div>

    <input
      v-else
      :value="modelValue"
      :name="fieldKey"
      type="text"
      class="form-input w-full"
      @focus="$emit('focus')"
      @input="$emit('preview', $event.target.value)"
      @blur="commit($event.target.value)">
  </label>
</template>

<script setup>
import { computed, ref } from 'vue'
import ImageField from './ImageField.vue'
import RichTextField from './RichTextField.vue'
import StylePopover from './StylePopover.vue'

const props = defineProps({
  fieldKey: { type: String, required: true },
  defn: { type: Object, required: true },
  modelValue: { type: [String, Number], default: '' },
  // The template's pre-customer default for this field. Comparing
  // modelValue === defaultValue tells us whether the customer has
  // personalized it yet — drives the "Template default" hint badge.
  defaultValue: { type: [String, Number], default: '' },
  // Style override delta (color / background-color / font-size). Parent
  // owns the data; we round-trip it through the StylePopover.
  styleOverride: { type: Object, default: null },
  // Alt text for image fields (stored alongside the URL by the parent).
  altValue: { type: String, default: '' },
  // Brand palette colors offered as swatches in the style popover.
  brandSwatches: { type: Array, default: () => []},
  // Site pages offered in the link picker for url-type fields.
  pageLinks: { type: Array, default: () => []}
})

const emit = defineEmits(['update:modelValue', 'update:styleOverride', 'update:alt', 'preview', 'focus', 'generate'])

// Link picker (url fields): is the current value one of the site pages, or a
// custom URL? customMode sticks once the user chooses "Custom URL…".
const customMode = ref(false)
const isPageLink = computed(() => props.pageLinks.some((p) => p.href === props.modelValue))
const linkMode = computed(() => (customMode.value || (!!props.modelValue && !isPageLink.value) ? 'custom' : 'page'))
function onLinkSelect(value) {
  if (value === '__custom__') { customMode.value = true; return }
  customMode.value = false
  emit('update:modelValue', value)
}
const VALID_LINK = /^(https?:\/\/|mailto:|tel:|#|\/|[\w-]+(\/[\w-]+)*\.html)/i
const linkWarning = computed(() => {
  if (linkMode.value !== 'custom' || !props.modelValue) return ''
  return VALID_LINK.test(String(props.modelValue).trim()) ? '' : 'This doesn’t look like a URL, phone, email, or page link.'
})

// Only emit on blur when the value actually changed — a no-op blur (e.g.
// focus leaving an untouched field because the user clicked elsewhere in the
// preview) must NOT trigger a save + preview reload.
function commit(value) {
  if (String(value ?? '') !== String(props.modelValue ?? '')) emit('update:modelValue', value)
}

// Style popover hides itself for richtext (handled via TipTap toolbar)
// and color-typed fields (the value IS a color — no separate styling).
const showStyleControls = computed(() => !['richtext', 'color'].includes(props.defn.type))

// Multiline is driven by the schema (type 'textarea' or a `multiline` flag) or
// a long-text field name — NOT the current value length, so a field doesn't
// silently morph from input to textarea once its text grows.
const LONG_TEXT_KEY = /(body|description|subtitle|sub_head|subhead|lead|intro|about|bio|message|paragraph|content|excerpt|quote|summary|tagline|answer|blurb|copy|note)/i
const isLongText = computed(() => {
  if (['url', 'color', 'image', 'richtext'].includes(props.defn.type)) return false
  return props.defn.type === 'textarea' ||
    props.defn.multiline === true ||
    LONG_TEXT_KEY.test(props.fieldKey)
})

// Show the hint only when the current value still matches the default —
// and only when there's actually a default to fall back on. Image fields
// also get the badge so customers know hero photos are placeholders.
const isTemplateDefault = computed(() => {
  if (!props.defaultValue) return false
  return String(props.modelValue || '').trim() === String(props.defaultValue || '').trim()
})
</script>
